/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { effects } from "./effects";
import { StyledText } from "../assets/typography/typography";
import { colors } from "./colors";
import { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import {
  ButtonActionForwardedRef,
  ButtonPrimaryForwardedRef,
} from "./NewButtons";
import { PopoverBase } from "./PopoverBase";
import { CheckListField } from "./forms/fields/CheckListField";
import { CloseButton } from "@headlessui/react";

const wrapperStyle = css`
  display: flex;
  flex-direction: row;
  align-items: center;
  box-shadow: ${effects.buttonPrimary};
  border-radius: 4px;
  overflow: hidden;
  width: fit-content;
  height: 100%;
`;

const buttonsWrapperStyle = css`
  display: flex;
  flex-direction: row;
  align-items: center;
  border-top: 1px solid ${colors.greyBorder};
  position: sticky;
  background-color: ${colors.white};
  bottom: 0;
  padding: 4px;
  gap: 8px;
  margin-top: auto;
  justify-content: space-between;
`;

const optionsWrapperStyle = css`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 4px;
  gap: 2px;
`;

type Props = {
  label: string;
  values: string[];
  onChange: (values: string[]) => void;
  removeFilter: () => void;
  options: {
    value: string;
    label: string;
  }[];
  singleSelect?: boolean;
};

const baseStyle = css`
  width: fit-content;
  background-color: ${colors.white};
  display: flex;
  flex-direction: row;
  align-items: center;
  border: none;
  padding: 4px 12px;
  height: 100%;
  gap: 8px;
`;

const buttonStyle = css`
  ${baseStyle}
  cursor: pointer;
  color: ${colors.textPrimary};
  &:hover {
    background-color: ${colors.whiteHover};
  }
`;

const optionsButtonStyle = css`
  ${buttonStyle}
  border-right: 1px solid ${colors.greyBorder};
`;

const filterWrapperStyle = css`
  display: flex;
  flex-direction: column;
  position: relative;
  height: 100%;
`;

const additionalPanelStyle = css`
  height: 300px;
  overflow-y: scroll;
  min-width: 200px;
`;

export const FilterItem = ({
  label,
  values,
  options,
  onChange,
  removeFilter,
  singleSelect = false,
}: Props) => {
  const [filters, setFilters] = useState<string[]>(values);

  const buttonLabel =
    values.length === 1
      ? options.find((o) => o.value === values[0])?.label || ""
      : values.length + " options";

  const wrappedElementStyle = css`
    ${baseStyle}
    border-right: 1px solid ${colors.greyBorder};
  `;

  useEffect(() => {
    setFilters(values);
  }, [values]);

  return (
    <div css={wrapperStyle}>
      <div css={wrappedElementStyle}>
        <StyledText className="bodyTextRegular mb-0" color={colors.textPrimary}>
          {label}
        </StyledText>
      </div>

      <div css={wrappedElementStyle}>
        <StyledText
          className="bodyTextRegular mb-0"
          color={colors.textSecondary}
        >
          is
        </StyledText>
      </div>
      <PopoverBase
        anchor="bottom start"
        additionalPanelStyling={additionalPanelStyle}
        buttonComponent={
          <button css={optionsButtonStyle}>
            <StyledText className="bodyTextRegular mb-0">
              {buttonLabel}
            </StyledText>
            <StyledText className="bodyTextRegular mb-0">
              <FontAwesomeIcon icon={solid("chevron-down")} />
            </StyledText>
          </button>
        }
      >
        <div css={filterWrapperStyle}>
          <div css={optionsWrapperStyle}>
            {options.map((option, i) => (
              <CheckListField
                key={i}
                label={option.label}
                value={option.value}
                onChange={(e) => {
                  if (e.target.checked) {
                    if (singleSelect) {
                      setFilters([option.value]);
                    } else {
                      setFilters((prev) => [...prev, option.value]);
                    }
                    return;
                  }
                  if (singleSelect) {
                    setFilters([]);
                  } else {
                    setFilters((prev) =>
                      prev.filter((f) => f !== option.value)
                    );
                  }
                }}
                checked={filters.includes(option.value)}
              />
            ))}
          </div>
          <div css={buttonsWrapperStyle}>
            <CloseButton
              buttonSize="small"
              as={ButtonPrimaryForwardedRef}
              onClick={removeFilter}
            >
              Clear
            </CloseButton>
            <CloseButton
              buttonSize="small"
              as={ButtonActionForwardedRef}
              onClick={() => {
                onChange(filters);
              }}
            >
              Save
            </CloseButton>
          </div>
        </div>
      </PopoverBase>
      <button css={buttonStyle} onClick={removeFilter}>
        <FontAwesomeIcon icon={solid("xmark")} />
      </button>
    </div>
  );
};
